/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip';
import Tooltip from 'components/@extended/Tooltip';
import SupervisedAndResponsibleEmployees from 'components/misc/SupervisedAndResponsibleEmployees.tsx';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Checkbox } from 'components/ui/checkbox';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { BillableMatterListItem } from 'types/billable-matters';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

import { ClientParty } from 'types/matters';
import { getBudgetInfo } from '../invoices/util/invoiceUtil';

export interface GroupedMatters {
    matters: BillableMatterListItem[];
    client: ClientParty;
}

type Props = {
    matterGroup: GroupedMatters;
    classifiers: Record<string, ClassifierData>;
    handleMatterSelect: (id: number, selected: boolean) => void;
    selectedIds: Set<number>;
    handleClientSelectAll: (matterIds: number[], selected: boolean) => void;
};

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

// The figure columns read right-aligned, header and body alike.
const RIGHT = 'text-right';

export const BillableMattersGroupTable = ({
    matterGroup,
    classifiers,
    handleMatterSelect,
    selectedIds,
    handleClientSelectAll,
}: Props) => {
    const effectiveFormat = useEffectiveDateFormat();
    const isMultipleDraftInvoicesPerCaseEnabled = useIsFeatureEnabled(FeatureFlag.MULTIPLE_DRAFT_INVOICES_PER_CASE);

    const selectableMatterIds = useMemo(
        () =>
            matterGroup.matters
                .filter((matter) => isMultipleDraftInvoicesPerCaseEnabled || !matter.matterInvoices)
                .map((matter) => matter.id),
        [matterGroup.matters, isMultipleDraftInvoicesPerCaseEnabled]
    );

    const selectedInGroupCount = selectableMatterIds.filter((id) => selectedIds.has(id)).length;
    const allSelectedInGroup = selectableMatterIds.length > 0 && selectedInGroupCount === selectableMatterIds.length;
    const someSelectedInGroup = selectedInGroupCount > 0 && !allSelectedInGroup;

    const createData = useCallback(
        (matter: BillableMatterListItem) => {
            const name = (
                <RouterLink className={cn(LINK_CLASS, 'font-semibold')} to={`/matter/${matter.id}`}>
                    {matter.fullName}
                </RouterLink>
            );

            const matterInvoices = (
                <div className="flex flex-row flex-wrap gap-2">
                    {matter.matterInvoices ? (
                        matter.matterInvoices.invoiceDetails.map((invoiceDetail) => (
                            <RecordLink
                                className="w-fit no-underline"
                                to={`/invoice/${invoiceDetail.invoiceId}`}
                                key={invoiceDetail.invoiceId}
                                label={invoiceDetail.invoiceNumber}
                            >
                                <StatusChip type="link" label={invoiceDetail.invoiceNumber} />
                            </RecordLink>
                        ))
                    ) : (
                        <StatusChip type="inactive" label={'No draft'} />
                    )}
                </div>
            );

            const employees = (
                <SupervisedAndResponsibleEmployees
                    supervisedByEmployees={matter.supervisedByEmployee}
                    responsibleEmployees={matter.responsibleEmployee}
                />
            );
            const uninvoicedExpensesInOriginalCurrency = (
                <p className="text-body1">
                    <MoneyDisplay
                        amount={matter.currencyDetails?.uninvoicedExpensesInOriginalCurrency ?? 0}
                        currency={matter.currencyDetails?.originalCurrency ?? ''}
                    />
                </p>
            );
            const uninvoicedExpensesInBaseCurrency = (
                <span className="text-caption text-fg-secondary">
                    <MoneyDisplay
                        amount={matter.currencyDetails?.uninvoicedExpensesInBaseCurrency ?? 0}
                        currency={matter.currencyDetails?.baseCurrency ?? ''}
                    />
                </span>
            );
            const uninvoicedHours = <HoursDisplay value={matter.matterStats?.uninvoicedTimeInHours} />;
            const uninvoicedAmountInOriginalCurrency = (
                <p className="text-body1">
                    <MoneyDisplay
                        amount={
                            (matter.currencyDetails?.uninvoicedAmountInOriginalCurrency ?? 0) +
                            (matter.currencyDetails?.uninvoicedExpensesInOriginalCurrency ?? 0)
                        }
                        currency={matter.currencyDetails?.originalCurrency ?? ''}
                    />
                </p>
            );
            const uninvoicedAmountInBaseCurrency = (
                <span className="text-caption text-fg-secondary">
                    <MoneyDisplay
                        amount={
                            (matter.currencyDetails?.uninvoicedAmountInBaseCurrency ?? 0) +
                            (matter.currencyDetails?.uninvoicedExpensesInBaseCurrency ?? 0)
                        }
                        currency={matter.currencyDetails?.baseCurrency ?? ''}
                    />
                </span>
            );
            const lastTc = matter.matterStats?.latestTimecardDate
                ? format(new Date(matter.matterStats?.latestTimecardDate), effectiveFormat)
                : '-';

            const lastExp = matter.latestExpenseDate
                ? format(new Date(matter.latestExpenseDate), effectiveFormat)
                : '-';

            const containsMissingJobRole = matter.matterStats?.containsMissingJobRole;

            const containsMissingHourlyPrice = matter.matterStats?.containsMissingHourlyPrice;

            const getTooltipTitle = (): string[] => {
                const texts: string[] = [];
                if (containsMissingJobRole) {
                    texts.push('billable-clients.contains-missing-job-role');
                }
                if (containsMissingHourlyPrice) {
                    texts.push('billable-clients.contains-missing-hourly-price');
                }
                return texts;
            };

            const nextInvoiceDate = matter.nextInvoicingDate
                ? format(new Date(matter.nextInvoicingDate), effectiveFormat)
                : '';

            const invoiceInterval = (
                <div className="flex flex-col">
                    <p className="text-body1">{nextInvoiceDate}</p>
                    <p className="text-fg-secondary text-xs leading-[1.375rem] lowercase">
                        {getClassifierText(classifiers['INVOICING_INTERVAL_TYPE'], matter.invoicingIntervalTypeCode)}
                    </p>
                </div>
            );

            const lastInvoice = matter.lastInvoice ? (
                <div className="flex flex-col items-end gap-1">
                    <RecordLink
                        className={LINK_CLASS}
                        to={`/invoice/${matter.lastInvoice.id}`}
                        label={matter.lastInvoice.invoiceNumber}
                    />
                    {/* The new design keeps the chip on the cell's bottom line, under the date. */}
                    <div className="new:order-last flex flex-row items-center gap-1">
                        <StatusChip
                            type="inactive"
                            label={getClassifierText(
                                classifiers['INVOICE_STATUS_TYPE'],
                                matter.lastInvoice.invoiceStatusTypeCode
                            )}
                        />
                    </div>
                    <span className="text-caption text-fg-secondary">
                        {format(new Date(matter.lastInvoice.invoiceDate), effectiveFormat)}
                    </span>
                </div>
            ) : (
                <p className="text-body1">-</p>
            );

            const invoicedByOffice = matter.invoicedByOffice ? (
                <RecordLink
                    className={LINK_CLASS}
                    to={`/office/${matter.invoicedByOffice.id}`}
                    label={`${matter.invoicedByOffice.name}${officeInactiveSuffix(matter.invoicedByOffice) ?? ''}`}
                />
            ) : (
                <p className="text-body1">-</p>
            );

            const budget = getBudgetInfo(matter, true) || <p className="text-body1">-</p>;

            return {
                name,
                matterInvoices,
                employees,
                uninvoicedHours,
                uninvoicedAmountInOriginalCurrency,
                uninvoicedAmountInBaseCurrency,
                uninvoicedExpensesInOriginalCurrency,
                uninvoicedExpensesInBaseCurrency,
                lastTc,
                lastExp,
                invoiceInterval,
                lastInvoice,
                invoicedByOffice,
                budget,
                containsMissingJobRole,
                containsMissingHourlyPrice,
                getTooltipTitle,
            };
        },
        [classifiers, effectiveFormat]
    );

    const tableContainerMemoized = useMemo(
        () => (
            <table className="w-full table-fixed text-left text-sm">
                <TableHeader className="[&_tr]:border-b-0">
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-12 pr-0')}>&nbsp;</TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[15%]')}>{t('matters.matter')}</TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'w-[8%]')}>
                            {t('billable-clients.case-responsible-persons')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[8%]')}>
                            {t('billable-clients.last-invoice')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[10%]')}>
                            {t('matters.invoiced-by-office')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[10%]')}>
                            {t('invoices.cap-fixed-fee')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[9%]')}>
                            {t('stats.uninvoiced-hours')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[10%]')}>
                            {t('stats.uninvoiced-expenses')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[10%]')}>
                            {t('stats.uninvoiced-amount')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[7%]')}>
                            {t('billable-clients.last-timecard')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[7%]')}>
                            {t('billable-clients.last-exp')}
                        </TableHead>
                        <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, RIGHT, 'w-[6%]')}>
                            {t('billable-clients.next-invoice-interval')}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {matterGroup.matters.map((matter: BillableMatterListItem) => {
                        const row = createData(matter);
                        let isSelectable = false;

                        if (isMultipleDraftInvoicesPerCaseEnabled) {
                            isSelectable = true;
                        } else if (!matter.matterInvoices) {
                            isSelectable = true;
                        }
                        const cellClass = cn(SIMPLE_TABLE_CELL_CLASS, 'align-top');
                        return (
                            <TableRow key={`row-${matter.id}`} className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableCell className={cn(cellClass, 'w-12 pr-0')}>
                                    {isSelectable ? (
                                        // Nudged onto the first line of the case name beside it.
                                        <Checkbox
                                            className="mt-[3px] ml-2"
                                            variant="secondary"
                                            aria-label={matter.fullName}
                                            checked={selectedIds.has(matter.id)}
                                            onCheckedChange={(checked) => handleMatterSelect(matter.id, checked)}
                                        />
                                    ) : null}
                                </TableCell>
                                <TableCell className={cn(cellClass, 'w-[15%]')}>
                                    <div className="flex flex-row">
                                        <div className="flex flex-col">
                                            <div>{row.name}</div>
                                            <div className="flex flex-row">
                                                {row.containsMissingJobRole || row.containsMissingHourlyPrice ? (
                                                    <div className="text-fg-secondary mr-2">
                                                        <Tooltip text={row.getTooltipTitle()}>
                                                            <div className="w-fit">
                                                                <StatusChip
                                                                    icon={
                                                                        <PriorityHighRoundedIcon className="text-fg-error h-4 w-4" />
                                                                    }
                                                                    type="error"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                ) : null}
                                                {row.matterInvoices}
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className={cn(cellClass, 'w-[8%]')}>{row.employees}</TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[8%]')}>{row.lastInvoice}</TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[10%]')}>
                                    {row.invoicedByOffice}
                                </TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[10%]')}>{row.budget}</TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[9%]')}>{row.uninvoicedHours}</TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[10%]')}>
                                    {row.uninvoicedExpensesInOriginalCurrency}
                                    {row.uninvoicedExpensesInBaseCurrency}
                                </TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[10%]')}>
                                    {row.uninvoicedAmountInOriginalCurrency}
                                    {row.uninvoicedAmountInBaseCurrency}
                                </TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[7%] whitespace-nowrap')}>
                                    {row.lastTc}
                                </TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[7%] whitespace-nowrap')}>
                                    {row.lastExp}
                                </TableCell>
                                <TableCell className={cn(cellClass, RIGHT, 'w-[6%] whitespace-nowrap')}>
                                    {row.invoiceInterval}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </table>
        ),
        [matterGroup.matters, selectedIds, handleMatterSelect, createData, isMultipleDraftInvoicesPerCaseEnabled]
    );
    return (
        <div className="border-divider bg-paper overflow-hidden rounded-[4px] border not-first-of-type:mt-6">
            <div className="border-divider bg-grey-50 flex items-center border-b px-4 py-[9px]">
                {/* min-h-8 sets a selectable group's band height; the 16px checkbox alone would leave it short. */}
                <div className={cn('text-h5 flex items-center', selectableMatterIds.length > 0 && 'min-h-8')}>
                    {selectableMatterIds.length > 0 && (
                        <Checkbox
                            className="mr-4 ml-2"
                            variant="secondary"
                            aria-label={t('forms.select-all')}
                            checked={allSelectedInGroup}
                            indeterminate={someSelectedInGroup}
                            onCheckedChange={(checked) => handleClientSelectAll(selectableMatterIds, checked)}
                        />
                    )}
                    {matterGroup?.client.id ? (
                        <PartyName party={matterGroup.client} isLink variant="readonly-other" showPartyNumber />
                    ) : (
                        <PartyName party={matterGroup.client} variant="readonly-other" showPartyNumber />
                    )}
                </div>
            </div>
            {tableContainerMemoized}
        </div>
    );
};
